import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppLoader from "components/AppLoader";
import CommonStoreHeader from "components/CommonStoreHeader";
import CommonFooter from "components/CommonFooter";
import appSettings from "appsettings.json";

function TermsAndConditions() {
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [termsPdfDataUrl, setTermsPdfDataUrl] = useState("");

  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);

  const apiBaseUrl = useMemo(() => {
    return appSettings.baseUrl;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadHelpFile = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}${appSettings.endpoints.helpFileBase64}`);
        if (!response.ok) {
          throw new Error("Failed to load help file");
        }

        const data = await response.json();
        const base64 = data?.data;
        const contentType = data?.contentType || "application/pdf";

        if (!base64) {
          throw new Error("Invalid help file payload");
        }

        if (!isCancelled) {
          setTermsPdfDataUrl(`data:${contentType};base64,${base64}`);
        }
      } catch (loadError) {
        console.error("Help file load error:", loadError);
        if (!isCancelled) {
          setError("Unable to load terms and conditions. Please try again.");
        }
      }
    };

    loadHelpFile();

    return () => {
      isCancelled = true;
    };
  }, [apiBaseUrl]);

  const handleSubmit = async () => {
    if (!token) {
      navigate("/authentication/mobile-sign-in");
      return;
    }

    if (!isChecked) return;

    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${apiBaseUrl}${appSettings.endpoints.acceptToc}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to accept terms and conditions");
      }

      localStorage.setItem("isTocAccepted", "true");
      navigate("/");
    } catch (submitError) {
      console.error("Accept TOC error:", submitError);
      setError("Unable to accept terms and conditions. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isTocAccepted");
    navigate("/authentication/mobile-sign-in");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
      }}
    >
      <CommonStoreHeader />

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5, md: 3.5 },
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: "auto", md: "calc(100vh - 230px)" },
        }}
      >
        <Card
          sx={{
            width: { xs: "96%", sm: "92%", md: "86%", lg: "80%" },
            maxWidth: 1200,
            mx: "auto",
            borderRadius: 2.3,
            border: "1px solid #fecdd3",
            boxShadow: "0 18px 36px rgba(15, 23, 42, 0.22)",
            background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 44%)",
          }}
        >
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #b71c2a 0%, #9f1239 100%)",
            }}
            borderRadius="xl"
            coloredShadow="error"
            mx={2}
            mt={-3}
            p={2}
            mb={1.5}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              Terms and Conditions
            </MDTypography>
          </MDBox>

          <MDBox pt={3} pb={3} px={{ xs: 2.2, md: 3 }}>
            <MDBox
              sx={{
                border: "1px solid #fecdd3",
                borderRadius: "8px",
                overflow: "hidden",
                height: { xs: "54vh", sm: "62vh", md: "68vh" },
                minHeight: 460,
                maxHeight: 820,
                mb: 2,
                position: "relative",
                zIndex: 0,
                backgroundColor: "#ffffff",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.9)",
              }}
            >
              <iframe
                title="Terms and Conditions"
                src={termsPdfDataUrl}
                width="100%"
                height="100%"
                style={{ border: "none", display: "block" }}
              />
            </MDBox>

            {isLoggedIn ? (
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                  sx={{
                    color: "#b71c2a",
                    "&.Mui-checked": {
                      color: "#b71c2a",
                    },
                  }}
                />
                <MDTypography
                  variant="button"
                  sx={{ cursor: "pointer", color: "#334155", fontSize: "0.96rem" }}
                  onClick={() => setIsChecked((prev) => !prev)}
                >
                  I have read and accept the Terms and Conditions.
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox mt={0.5}>
                <MDTypography variant="button" sx={{ color: "#475569", fontSize: "0.95rem" }}>
                  You can read the terms here. Log in to accept and continue checkout.
                </MDTypography>
              </MDBox>
            )}

            {error && (
              <MDBox mt={1} textAlign="center">
                <MDTypography variant="button" color="error" fontWeight="medium">
                  {error}
                </MDTypography>
              </MDBox>
            )}

            {isLoggedIn && (
              <>
                <MDBox mt={2}>
                  <MDButton
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={!isChecked || isSubmitting}
                    sx={{
                      background: "linear-gradient(135deg, #b71c2a 0%, #9f1239 100%)",
                      color: "#ffffff !important",
                      borderRadius: 1.4,
                      fontWeight: 800,
                      py: 1.25,
                      boxShadow: "0 10px 20px rgba(183, 28, 42, 0.26)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #991b1b 0%, #831843 100%)",
                      },
                      "&.Mui-disabled": {
                        background: "#fecdd3",
                        color: "#9f1239 !important",
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <AppLoader size={20} color="currentColor" />
                    ) : (
                      "Accept & Continue"
                    )}
                  </MDButton>
                </MDBox>
                <MDBox mt={1}>
                  <MDButton
                    variant="outlined"
                    fullWidth
                    onClick={handleReject}
                    sx={{
                      borderColor: "#fda4af",
                      color: "#b71c2a",
                      borderRadius: 1.4,
                      fontWeight: 700,
                      py: 1.1,
                      "&:hover": {
                        borderColor: "#fb7185",
                        backgroundColor: "#fff1f2",
                      },
                    }}
                  >
                    Reject & Logout
                  </MDButton>
                </MDBox>
              </>
            )}
          </MDBox>
        </Card>
      </Box>

      <CommonFooter />
    </Box>
  );
}

export default TermsAndConditions;
